import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Col, Row, Card } from "react-bootstrap";
import {
  datetimeFormats,
  inspectionTypes,
  machineExpiredStatus,
} from "../../constants/constants";
import MaterialReactTable, {
  type MRT_ColumnDef,
  type MRT_SortingState,
} from "material-react-table";
import { expirationRequest } from "../../services/dashboard";
import moment from "moment";
import { nameBasedProtectedRoutes } from "router/protected";
import { Pagination } from "@mui/material";

/**
 * Create a custom component which will render upcoming inspection list section in dashboard.
 * @param props
 * @returns Upcoming Inspection component
 */
export const Expirations = (props: any) => {
  const [sortColumn, setSortColumn] = useState("device_id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: sortColumn, desc: false },
  ]); // The default sorting data to show status column sort icon

  const [fromRows, setFromRows] = useState(0);
  const [toRows, setToRows] = useState(0);
  const [totalRow, setTotalRow] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchExpirationInspections({
      sortColumn: 0 in sorting ? sorting[0].id : sortColumn,
      sortDirection:
        0 in sorting
          ? sorting[0].desc === false
            ? "asc"
            : "desc"
          : sortDirection,
      page: currentPage,
    });
  }, [sorting,currentPage]);

  // Fetch Expire and upcomming expire inspection
  const fetchExpirationInspections = (params: any) => {
    setLoading(true);
    expirationRequest(
      {
        ...params,
      },
      props.userId
    ).then((response) => {
      setData(response.resultRows);
      setTotalPages(response.totalPages);
      setTotalRow(response.total);
      const fromRowsVal =
        response.resultRows.length > 0
          ? (response.page - 1) * response.limit + 1
          : 0;
      let toRowsVal = fromRowsVal + response.resultRows.length - 1;
      toRowsVal = toRowsVal > 0 ? toRowsVal : 0;
      setFromRows(fromRowsVal);
      setToRows(toRowsVal);
      setLoading(false);
    });
  };

  /* Column Definition requires for MaterialReactTable */
  type ExpirationInspection = {
    id: string;
    device_id: string;
    model_number: string;
    serial_number: number;
    job_location: string;
    inspection_type: string;
    inspection_id: number;
    Inspection: {
      inspection_date: string;
    };
    certificate: {
      machine_status: number;
    };
  };

  // Mentioning table columns belows.
  const tableColumns = useMemo<MRT_ColumnDef<ExpirationInspection>[]>(
    () => [
      {
        accessorKey: "device_id",
        header: "DEVICE ID",
      },
      {
        accessorKey: "model_number",
        header: "MODEL NO.",
      },
      {
        accessorKey: "serial_number",
        header: "SERIAL NO.",
      },
      {
        accessorKey: "job_location",
        header: "LOCATION",
      },
      {
        accessorKey: "inspection_type",
        header: "INSPECTION TYPE",
        Cell: ({ cell, row }) => {
          const inspection = inspectionTypes.find(
            (type) => row.original?.inspection_type == type.key
          );

          return inspection ? inspection?.value : row.original?.inspection_type;
        },
      },
      {
        accessorKey: "Inspection.inspection_date",
        header: "INSPECTION DATE",
        Cell: ({ cell, row }) => {
          return row.original?.Inspection?.inspection_date
            ? moment(row.original?.Inspection?.inspection_date).format(
                datetimeFormats.separatedDate
              )
            : "-";
        },
      },

      {
        accessorKey: "certificate.machine_status",
        header: "EXPIRATION",
        Cell: ({ cell, row }) => {
          return row.original?.certificate?.machine_status >= 0 ? (
            <span className="bg-light-pink text-pink text-center br-5 p-1 pe-2 ps-2 fs-15 d-inline-block">
              {row.original?.certificate?.machine_status} days
            </span>
          ) : (
            <span className="bg-light-red text-danger fs-15  p-1 pe-2 ps-2 cap-text">
              {machineExpiredStatus.expired}
            </span>
          );
        },
      },
      {
        // accessorKey: "id",
        header: "ACTION",
        enableSorting: false,
        Cell: ({ cell, row }) => (
          <button
            className="btn btn-inspection-req"
            onClick={() =>
              generateInspectionRequest(row.original?.inspection_id)
            }
          >
            Submit Inspection Request
          </button>
        ),
      },
    ],
    []
  );

  // navigation to generate inspection request for expire machine
  const generateInspectionRequest = (id) => {
    navigate(nameBasedProtectedRoutes.newInspectionRequest.path + "/" + id);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);

    //fetchUpcomingInspections({ page: page, month: selectedMonth });
  };

  return (
    <Row className="mb-4">
      <Col md={12}>
        <Card className="border-0">
          <Card.Header className="border-0 bg-default bg-theme black-color fs-20 fw-bold ls-2 btlr-14 btrr-14 pe-4 ps-4 pt-11">
            EXPIRATIONS (UPCOMING/EXPIRED)
          </Card.Header>
          <Card.Body className="pe-4 ps-4 pt-4">
            <div className="table-responsive dashboard-table-data">
              <MaterialReactTable
                muiTableProps={{
                  classes: {
                    root: "table custom-table border-none-table table-striped machine-custome-table",
                  },
                }}
                enableGlobalFilter={false}
                enableFullScreenToggle={false}
                enableDensityToggle={false}
                enableHiding={false}
                enableColumnFilters={false}
                enablePagination={false}
                onSortingChange={setSorting}
                manualSorting
                columns={tableColumns}
                data={data}
                state={{ sorting }}
              />
              {totalPages > 0 ? (
                <div className="paggination">
                  <p className="showing-text">
                    Showing {fromRows}-{toRows} of {totalRow}
                  </p>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    shape="rounded"
                    variant="outlined"
                    onChange={handlePageChange}
                  />
                </div>
              ) : (
                ""
              )}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};
